/* ==========================================================================
   Vita Performance – globales JavaScript (Vanilla, Web Components)
   - Dialoge/Drawer (data-dialog-open / data-dialog-close)
   - <header-menu>      Dropdown-Navigation mit Tastaturbedienung
   - <sticky-header>    blendet beim Hochscrollen ein
   - <scroll-top-button>
   - Einblenden beim Scrollen ([data-reveal])
   ========================================================================== */

window.Theme = window.Theme || {};

(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Dialoge ---------- */
  const openers = new WeakMap();

  Theme.openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    document.documentElement.classList.add('is-locked');
    if (opener) {
      openers.set(dialog, opener);
      opener.setAttribute('aria-expanded', 'true');
    }
  };

  Theme.closeDialog = (dialog) => {
    if (dialog && dialog.open) dialog.close();
  };

  const onDialogClose = (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    const opener = openers.get(dialog);
    if (opener) {
      opener.setAttribute('aria-expanded', 'false');
      opener.focus();
      openers.delete(dialog);
    }
    if (!document.querySelector('dialog[open]')) {
      document.documentElement.classList.remove('is-locked');
    }
  };

  // close-Events bubblen nicht, daher in der Capture-Phase abgreifen
  document.addEventListener('close', onDialogClose, true);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      if (dialog) {
        event.preventDefault();
        Theme.openDialog(dialog, opener);
      }
      return;
    }

    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      Theme.closeDialog(closer.closest('dialog'));
      return;
    }

    // Klick auf den Backdrop (= das Dialog-Element selbst) schließt
    if (event.target instanceof HTMLDialogElement && event.target.hasAttribute('data-light-dismiss')) {
      event.target.close();
    }
  });

  /* ---------- Dropdown-Navigation ---------- */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-menu-item]'));

      this.items.forEach((item) => {
        const toggle = item.querySelector('[data-menu-toggle]');
        if (!toggle) return;

        item.addEventListener('mouseenter', () => this.open(item));
        item.addEventListener('mouseleave', () => this.close(item));
        toggle.addEventListener('click', () => {
          item.hasAttribute('data-open') ? this.close(item) : this.open(item);
        });
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.close(item);
        });
        item.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && item.hasAttribute('data-open')) {
            this.close(item);
            toggle.focus();
          }
        });
      });
    }

    open(item) {
      this.items.forEach((other) => other !== item && this.close(other));
      item.setAttribute('data-open', '');
      item.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', 'true');
    }

    close(item) {
      item.removeAttribute('data-open');
      item.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false');
    }
  }

  /* ---------- Sticky Header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.section-header') || this;
      this.mode = this.dataset.sticky || 'none';
      this.lastY = window.scrollY;

      this.updateHeight = () => {
        document.documentElement.style.setProperty('--header-height', `${this.wrapper.offsetHeight}px`);
      };
      this.updateHeight();
      this.resizeObserver = new ResizeObserver(this.updateHeight);
      this.resizeObserver.observe(this.wrapper);

      if (this.mode === 'on_scroll_up') {
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      const y = window.scrollY;
      const delta = y - this.lastY;
      const hideAfter = this.wrapper.offsetHeight + this.wrapper.offsetTop;

      if (Math.abs(delta) < 6) return;

      if (delta > 0 && y > hideAfter && !this.contains(document.activeElement)) {
        this.wrapper.classList.add('is-hidden');
      } else if (delta < 0 || y <= hideAfter) {
        this.wrapper.classList.remove('is-hidden');
      }
      this.lastY = y;
    }
  }

  /* ---------- Scroll-to-top ---------- */
  class ScrollTopButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.onScroll = () => {
        this.hidden = window.scrollY < 400;
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.button?.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
        document.getElementById('MainContent')?.focus({ preventScroll: true });
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  /* ---------- Laufband anhalten / fortsetzen (WCAG 2.2.2) ---------- */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-marquee-toggle]');
    if (!toggle) return;
    const marquee = toggle.closest('.marquee');
    const paused = marquee.classList.toggle('is-paused');
    toggle.setAttribute('aria-pressed', String(paused));
    toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
  });

  /* ---------- Einblenden beim Scrollen ----------
     Elemente mit data-reveal sind ohne Skript, im Theme-Editor und bei
     Reduced-Motion immer sichtbar. Nur Elemente unterhalb des sichtbaren
     Bereichs werden vorbereitet (data-armed) und beim Eintreten einmalig
     eingeblendet. Verzögerung über data-reveal-delay (ms). */
  const initReveal = (root = document) => {
    if (reducedMotion() || (window.Shopify && Shopify.designMode) || !('IntersectionObserver' in window)) return;

    const targets = Array.from(root.querySelectorAll('[data-reveal]:not([data-armed]):not(.is-revealed)'));
    const viewportHeight = window.innerHeight;
    const pending = targets.filter((el) => el.getBoundingClientRect().top > viewportHeight * 0.92);
    if (!pending.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    pending.forEach((el) => {
      const delay = el.dataset.revealDelay;
      if (delay) el.style.setProperty('--reveal-delay', delay + 'ms');
      el.setAttribute('data-armed', '');
      observer.observe(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initReveal());
  } else {
    initReveal();
  }
  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));

  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };
  define('header-menu', HeaderMenu);
  define('sticky-header', StickyHeader);
  define('scroll-top-button', ScrollTopButton);
})();
